import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import { CommonButton } from '@/components/ui/common-button';
import { useToast } from '@/components/ui/use-toast';
import masterDataService from '@/services/masterDataService';
import { VEHICLE_TYPE_OPTIONS } from '../addvehicles/AddVehicleForm';

const EditVehicleForm = ({ vehicle, onSubmit, isSubmitting, onCancel }) => {
  const [formData, setFormData] = useState({
    id: '',
    vehicle_number: '',
    vehicle_type: '',
    capacity: '',
    transporter_id: '',
    is_active: true
  });
  const [errors, setErrors] = useState({});
  const [masterData, setMasterData] = useState({
    transporters: []
  });
  const [loading, setLoading] = useState({
    transporters: false
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (vehicle) {
      setFormData({
        id: vehicle.id || '',
        vehicle_number: vehicle.vehicleNumber || '',
        vehicle_type: vehicle.vehicleType || '',
        capacity: vehicle.capacity || '',
        transporter_id: vehicle.transporterId || '',
        is_active: vehicle.status === 'Active'
      });
    }
  }, [vehicle]);

  const fetchMasterData = async () => {
    try {
      setLoading(prev => ({ ...prev, transporters: true }));
      const transporters = await masterDataService.getTransporters(0, 500, false);
      setMasterData(prev => ({ ...prev, transporters }));
    } catch (error) {
      console.error('Failed to fetch transporters:', error);
      toast({
        title: "Error",
        description: "Failed to load transporters.",
        variant: "destructive",
      });
    } finally {
      setLoading(prev => ({ ...prev, transporters: false }));
    }
  };

  const transporterOptions = masterData.transporters.map(transporter => ({
    value: transporter.id,
    label: transporter.company_name || transporter.id
  }));


  const getSelectedTransporterLabel = () => {
    if (!formData.transporter_id || transporterOptions.length === 0) {
      return '';
    }
    
    const selectedTransporter = transporterOptions.find(
      option => option.value === formData.transporter_id
    );
    
    return selectedTransporter ? selectedTransporter.label : formData.transporter_id;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vehicle_number.trim()) {
      newErrors.vehicle_number = 'Vehicle number is required';
    }
    
    if (!formData.vehicle_type.trim()) {
      newErrors.vehicle_type = 'Vehicle type is required';
    }
    
    if (!formData.capacity) {
      newErrors.capacity = 'Capacity is required';
    } else if (isNaN(formData.capacity) || Number(formData.capacity) <= 0) {
      newErrors.capacity = 'Capacity must be a positive number';
    }
    
    if (!formData.transporter_id.trim()) {
      newErrors.transporter_id = 'Transporter ID is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const vehicleData = {
        id: formData.id,
        vehicle_number: formData.vehicle_number,
        vehicle_type: formData.vehicle_type,
        capacity: Number(formData.capacity),
        transporter_id: formData.transporter_id,
        is_active: formData.is_active
      };
      
      await onSubmit(vehicleData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CommonInput
          label="Vehicle Number"
          id="vehicle_number"
          value={formData.vehicle_number}
          onChange={(e) => handleChange('vehicle_number', e.target.value)}
          error={errors.vehicle_number}
          required
          placeholder="Enter vehicle number"
        />
        
        <CommonSelect
          label="Vehicle Type"
          id="vehicle_type"
          value={formData.vehicle_type}
          onValueChange={(value) => handleChange('vehicle_type', value)}
          options={
            formData.vehicle_type && !VEHICLE_TYPE_OPTIONS.some(o => o.value === formData.vehicle_type)
              ? [{ value: formData.vehicle_type, label: formData.vehicle_type }, ...VEHICLE_TYPE_OPTIONS]
              : VEHICLE_TYPE_OPTIONS
          }
          error={errors.vehicle_type}
          required
          placeholder="Select vehicle type"
        />
        
        <CommonInput
          label="Capacity (in tons)"
          id="capacity"
          type="number"
          value={formData.capacity}
          onChange={(e) => handleChange('capacity', e.target.value)}
          error={errors.capacity}
          required
          placeholder="Enter capacity"
        />
        
        <div>
          <CommonInput
            label="Transporter"
            id="transporter_display"
            value={getSelectedTransporterLabel()}
            readOnly
            placeholder="Transporter"
          />
          <input 
            type="hidden" 
            name="transporter_id" 
            value={formData.transporter_id} 
          />
        </div>
        
        <div className="flex items-center justify-between">
          <CommonSelect
            label="Status"
            id="is_active"
            value={formData.is_active}
            onValueChange={(value) => handleChange('is_active', value === 'true' || value === true)}
            options={[{ value: true, label: 'Active' }, { value: false, label: 'Inactive' }]}
            placeholder="Select status"
          />
        </div>
      </div>
      
      <div className="flex justify-end space-x-3 pt-5">
        <CommonButton type="button" variant="outline" onClick={onCancel}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" variant="default" loading={isSubmitting}>
          Update Vehicle
        </CommonButton>
      </div>
    </form>
  );
};

export default EditVehicleForm;